import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {SchemaWithClient} from '../../types/SchemaWithClient';
import {Entity} from '../../types/entities/Entity';
import {
  Button as MUIButton,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableContainer,
  Typography,
} from '@mui/material';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import {SlidingPaneEdit} from '../Page/SlidingPaneEdit';
import api from '../../services/api';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import toast from 'react-hot-toast';
import TableBody from '@mui/material/TableBody';
import {SPNumberField} from '../Table/fields/SPNumberField';
import {SPSelectField} from '../Table/fields/SPSelectField';
import {enumItems} from '../../schema/enumItems';
import {formatNumber} from '../../reducer/actions/formatNumber';
import {formatPrice} from '../../reducer/actions/formatPrice';
import {RefreshRounded} from '@mui/icons-material';
import {SPSelectWithOutAutocomplete} from '../Table/fields/SPSelectWithOutAutocompleteField';

type Props<T extends Entity> = {
  schema: SchemaWithClient<T>;
};

export const Finance = ({
                          schema,
                          values,
                          fullWidth,
                          onChange,
                        }: {
  schema: any;
  values: any;
  fullWidth: boolean;
  onChange: (value: any, column: string) => void;
}): ReactElement => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [properties, setProperties] = useState<any>(undefined);
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});
  const [mnbRates, setMnbRates] = useState<any>(false);
  const [financeData, setFinanceData] = useState<any>(values?.finances);
  const [loading, setLoading] = useState(false);

  const groupSize = Number(values?.group_size || values?.group?.size || 1);
  const baseCurrency = values?.group?.base_currency || {id: 1, name: 'EUR'};

  const fetchData = useCallback(() => {
    if (!values?.id) return;

    setLoading(true);
    api.get(`/activity-reservations/${values.id}`)
      .then((res: any) => {
        setFinanceData(res.finances);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [values?.id]);

  const fetchMnbRates = useCallback(() => {
    if (!values?.group?.id || !values?.id) return;

    const searchParams = new URLSearchParams({
      activity: values.id,
    });

    api.get(`/mnb-rates/group/${values.group.id}`, searchParams).then((res: any) => {
      setMnbRates(res);
    });
  }, [values?.id, values?.group?.id]);

  useEffect(() => {
    // fetchData();
    fetchMnbRates();
  }, [fetchMnbRates]);

  useEffect(() => {
    setFinanceData(values?.finances);
  }, [values?.finances]);

  const calculatedFinance = useMemo(() => {
    if (!financeData) return null;

    const finalPrice = Number(financeData?.final_price || 0);
    const finalPricePP = Number(financeData?.final_price_pp || 0);

    const finalPriceCurrency = financeData?.final_price_currency || financeData?.original_price_currency || {id: 1, name: 'EUR'};
    const finalPriceCurrencyId = finalPriceCurrency?.id;
    const finalPriceCurrencyName = finalPriceCurrency?.name || 'EUR';

    const baseCurrencyId = baseCurrency?.id;
    const baseCurrencyName = baseCurrency?.name || 'EUR';

    let finalPriceInBaseCurrency = finalPrice;

    if (mnbRates && finalPriceCurrencyId !== baseCurrencyId) {
      if (finalPriceCurrencyName === 'HUF' && baseCurrencyName === 'EUR') {
        finalPriceInBaseCurrency = finalPrice / Number(mnbRates?.EUR || 1);
      } else if (finalPriceCurrencyName === 'EUR' && baseCurrencyName === 'HUF') {
        finalPriceInBaseCurrency = finalPrice * Number(mnbRates?.EUR || 1);
      }
    }

    const totalCostInSelectedCurrency =
      finalPriceCurrencyName === 'EUR'
        ? Number(financeData?.cost?.EUR || 0)
        : Number(financeData?.cost?.HUF || 0);

    const calculatedProfit = finalPrice - totalCostInSelectedCurrency;

    return {
      ...financeData,
      final_price: finalPrice,
      final_price_pp: finalPricePP,
      final_price_currency: finalPriceCurrency,
      final_price_in_base_currency: finalPriceInBaseCurrency,
      calculated_profit: calculatedProfit,
    };
  }, [financeData, mnbRates, baseCurrency]);

  const handleChangeCost = (value: any, type: string) => {
    const numericValue = Number(value || 0);

    setFinanceData((prev: any) => {
      if (!prev) return prev;

      if (type === 'pp') {
        const newFinalCost = numericValue * groupSize;
        onChange(newFinalCost, 'final_price');

        return {
          ...prev,
          final_price_pp: numericValue,
          final_price: newFinalCost,
        };
      }

      const newFinalPPCost = groupSize ? numericValue / groupSize : numericValue;
      onChange(numericValue, 'final_price');

      return {
        ...prev,
        final_price: numericValue,
        final_price_pp: newFinalPPCost,
      };
    });
  };

  const handleCurrencyChange = (value: any) => {
    setFinanceData((prev: any) => ({
      ...prev,
      final_price_currency: value,
    }));

    onChange(value, 'final_price_currency');
  };

  const renderCostRow = (cost: any, index: number) => (
    <TableRow key={`${cost?.name || 'cost'}-${cost?.currency?.name || 'ccy'}-${index}`}>
      <TableCell>{cost?.name}</TableCell>
      <TableCell >
        {cost?.price_pp ? formatNumber(cost?.price_pp) : ''}
      </TableCell>
      <TableCell >
        {cost?.price ? formatNumber(cost?.price) : ''}
      </TableCell>
      <TableCell>{cost?.currency?.name}</TableCell>
      <TableCell >
        {cost?.price_in_base_currency ? formatNumber(cost?.price_in_base_currency) : ''}
      </TableCell>
    </TableRow>
  );

  const isBaseCurrencyEur = baseCurrency?.name === 'EUR';
  const finalCurrencyIsEur = calculatedFinance?.final_price_currency?.name === 'EUR';

  return (
    <>
      <Stack
        sx={{
          mb: '20px',
          width: '100%',
        }}
        gap={2}
        direction={'row'}
        justifyContent={'space-between'}
      >
        <Stack direction={'row'} gap={1}>
          <Stack direction="row" gap={1} alignItems={'center'}>
            <Typography
              sx={{
                width: '200px',
              }}
            >
              Base Currency
            </Typography>
            <SPSelectField
              onChange={() => {}}
              schema={enumItems.currency}
              defaultValue={baseCurrency}
              forcedValue={undefined}
              disabled={true}
              isMissing={false}
              accordingToValue={undefined}
            />
          </Stack>

          <Stack direction="row" alignItems={'center'} gap={1}>
            <Typography>MNB</Typography>

            <Chip
              label={`€ ${mnbRates?.EUR || '-'}`}
              variant="outlined"
            />
          </Stack>
        </Stack>

        <Stack direction="row" alignItems={'center'} gap={1}>
          {loading ? (
            <CircularProgress size={'20px'} />
          ) : (
            <Tooltip title={'Refresh'}>
              <IconButton
                onClick={() => {
                  fetchData();
                }}
                color={'primary'}
              >
                <RefreshRounded />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <TableContainer
        component={Paper}
        sx={{
          width: '100%',
          opacity: loading ? 0.5 : 1,
          pointerEvents: loading ? 'none' : 'all',
        }}
      >
        <Table aria-label="simple table" size="small">
          <TableHead
            sx={{
              '& .MuiTableCell-root': {
                color: 'inherit',
                border: 'none',
              },
              '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                border: 'none',
                fontSize: '14px',
                p: '10px',
              },
              '& .MuiTableRow-root .MuiTableCell-root .MuiFormControl-root.MuiTextField-root': {
                background: '#f9f9f9',
                boxShadow: 'inset 0px 0px 1px #a7a7a7',
              },
              background: '#256861',
              color: '#fff',
            }}
          >
            <TableRow>
              <TableCell></TableCell>
              <TableCell >PP</TableCell>
              <TableCell >SUM</TableCell>
              <TableCell>CCY</TableCell>
              <TableCell >
                SUM in {baseCurrency?.name || 'EUR'}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody
            sx={{
              '& .MuiTableCell-root': {
                p: '5px 10px',
              },
              '& .MuiInputBase-root.MuiOutlinedInput-root': {
                paddingRight: '0px',
              },
            }}
          >
            <TableRow>
              <TableCell>Original Price</TableCell>
              <TableCell>
                {formatNumber(calculatedFinance?.original_price_pp)}
              </TableCell>
              <TableCell>
                {formatNumber(calculatedFinance?.original_price)}
              </TableCell>
              <TableCell>
                {calculatedFinance?.original_price_currency?.name}
              </TableCell>
              <TableCell>
                {calculatedFinance?.original_price_in_base_currency
                  ? formatNumber(calculatedFinance?.original_price_in_base_currency)
                  : ''}
              </TableCell>
            </TableRow>

            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  fontWeight: 'bold',
                },
                '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  background: '#f9f9f9',
                  boxShadow: 'inset 0px 0px 1px #5b5b5b',
                  '& .MuiInputBase-input': {
                    fontWeight: 'bold',
                  },
                },
              }}
            >
              <TableCell>
                <b>Final Price</b>
              </TableCell>

              <TableCell>
                <SPNumberField
                  withOutEndAdornment={true}
                  isActive
                  defaultValue={calculatedFinance?.final_price_pp}
                  onChange={(value) => {
                    handleChangeCost(value, 'pp');
                  }}
                />
              </TableCell>

              <TableCell>
                <SPNumberField
                  withOutEndAdornment={true}
                  isActive
                  defaultValue={calculatedFinance?.final_price}
                  onChange={(value) => {
                    handleChangeCost(value, 'full');
                  }}
                />
              </TableCell>

              <TableCell>
                <SPSelectWithOutAutocomplete
                  onChange={(value) => {
                    handleCurrencyChange(value);
                  }}
                  schema={enumItems.currency}
                  defaultValue={calculatedFinance?.final_price_currency}
                  forcedValue={undefined}
                  disabled={false}
                  isMissing={false}
                  accordingToValue={undefined}
                />
              </TableCell>

              <TableCell>
                {calculatedFinance?.final_price_in_base_currency
                  ? formatPrice(
                    calculatedFinance?.final_price_in_base_currency,
                    isBaseCurrencyEur,
                    true
                  )
                  : ''}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{background: '#f3f3f3'}} colSpan={5}>
                &nbsp;
              </TableCell>
            </TableRow>

            {calculatedFinance?.guide_costs?.costs?.map(renderCostRow)}
            {calculatedFinance?.service_costs?.costs?.map(renderCostRow)}

            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  fontWeight: 'bold',
                },
                '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  background: '#f9f9f9',
                  boxShadow: 'inset 0px 0px 1px #5b5b5b',
                  '& .MuiInputBase-input': {
                    fontWeight: 'bold',
                  },
                },
              }}
            >
              <TableCell>
                <b>Total Costs</b>
              </TableCell>

              <TableCell>
                {calculatedFinance?.total_costs_value_pp?.EUR
                  ? formatPrice(calculatedFinance?.total_costs_value_pp?.EUR, true)
                  : ''}
                <br />
                {calculatedFinance?.total_costs_value_pp?.HUF
                  ? formatPrice(calculatedFinance?.total_costs_value_pp?.HUF, false)
                  : ''}
              </TableCell>

              <TableCell>
                {calculatedFinance?.total_costs_value?.EUR
                  ? formatPrice(calculatedFinance?.total_costs_value?.EUR, true)
                  : ''}
                <br />
                {calculatedFinance?.total_costs_value?.HUF
                  ? formatPrice(calculatedFinance?.total_costs_value?.HUF, false)
                  : ''}
              </TableCell>

              <TableCell></TableCell>

              <TableCell >
                {calculatedFinance?.cost?.EUR
                  ? formatPrice(calculatedFinance?.cost?.EUR, true)
                  : ''}
                <br />
                {calculatedFinance?.cost?.HUF
                  ? formatPrice(calculatedFinance?.cost?.HUF, false)
                  : ''}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{background: '#f3f3f3'}} colSpan={5}>
                &nbsp;
              </TableCell>
            </TableRow>

            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  fontWeight: 'bold',
                },
                '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  background: '#f9f9f9',
                  boxShadow: 'inset 0px 0px 1px #5b5b5b',
                  '& .MuiInputBase-input': {
                    fontWeight: 'bold',
                  },
                },
              }}
            >
              <TableCell>
                <b>Profit</b>
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell >
                {calculatedFinance
                  ? formatPrice(
                    calculatedFinance.calculated_profit,
                    finalCurrencyIsEur
                  )
                  : ''}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {openConfirmDialog && (
        <Dialog
          open={openConfirmDialog}
          onClose={() => {
            setOpenConfirmDialog(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {'Are you sure you want to delete it?'}
          </DialogTitle>
          <DialogActions>
            <MUIButton
              onClick={() => {
                setOpenConfirmDialog(false);
              }}
            >
              Cancel
            </MUIButton>
            <MUIButton
              variant={'contained'}
              color={'error'}
              onClick={() => {
                const deletedRow = openConfirmDialog?.row;
                openConfirmDialog?.schema.client
                  .delete(deletedRow.id as number, {id: deletedRow.id})
                  .then((res: any) => {
                    const tempProperties = [...properties];

                    const newRows = tempProperties.filter((contact: any) => {
                      return contact?.id !== deletedRow?.id;
                    });
                    setProperties(newRows);
                    toast.success(
                      `Successfully deleted ${schema.name}: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`
                    );
                    setOpenConfirmDialog(false);
                  })
                  .catch((e: any) => {
                    console.log(e);
                    toast.error(
                      `Error: Unable to delete ${schema.name}: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`
                    );
                  });
              }}
              autoFocus
            >
              Delete
            </MUIButton>
          </DialogActions>
        </Dialog>
      )}

      {openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={() => {
            setOpenedSlidingPane({});
            fetchData();
            toast.success(`Successfully updated ${schema.name}`);
          }}
          propertiesFromTable={
            openedSlidingPane?.row ? openedSlidingPane?.row : {}
          }
          onClose={() => {
            setOpenedSlidingPane({});
          }}
        />
      )}
    </>
  );
};